/**
 * ShiftBar
 *
 * Phase 4 free-text co-writer. A small docked input where the musician asks for
 * a shift ("darker", "more like the opening"). The reply is rendered by the
 * shared ReviewQueue component (extracted from this file for Phase 6 — see
 * docs/design/STEAL-SPEC.md — so Steal from Masters' "voice it like {name}"
 * can feed the exact same queue). ShiftBar itself now only owns the ask
 * input and pushes results into review-queue-store.
 */
import React, { useCallback, useState } from 'react';
import type { Chord, MusicalKey, Mode } from '@/types';
import { requestShift, ShiftError } from '@/services/shift-service';
import { useReviewQueueStore } from '@/store/review-queue-store';
import { ReviewQueue } from './ReviewQueue';
import styles from './ShiftBar.module.css';

interface ShiftBarProps {
  chords: Chord[];
  currentKey: MusicalKey;
  currentMode: Mode;
  /** Apply a single kept step through the shared moves flow. */
  onApplyStep: (moveId: string, index: number) => 'applied' | 'not-applicable';
}

const ShiftBarComponent: React.FC<ShiftBarProps> = ({
  chords,
  currentKey,
  currentMode,
  onApplyStep,
}) => {
  const [instruction, setInstruction] = useState('');
  const [loading, setLoading] = useState(false);
  const loadPlan = useReviewQueueStore((s) => s.loadPlan);
  const loadError = useReviewQueueStore((s) => s.loadError);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const text = instruction.trim();
      if (!text || loading) return;

      setLoading(true);
      try {
        const response = await requestShift(chords, currentKey, currentMode, text);
        loadPlan(response.plan, response.summary);
      } catch (err) {
        const message =
          err instanceof ShiftError
            ? err.message
            : "the shift didn't come through — try again.";
        loadError(message);
      } finally {
        setLoading(false);
      }
    },
    [instruction, loading, chords, currentKey, currentMode, loadPlan, loadError]
  );

  return (
    <div className={styles.shiftBar}>
      <ReviewQueue onApplyStep={onApplyStep} />

      <form className={styles.form} onSubmit={handleSubmit}>
        <input
          type="text"
          className={styles.input}
          value={instruction}
          maxLength={300}
          placeholder="ask for a shift — darker, calmer, more like the opening"
          onChange={(e) => setInstruction(e.target.value)}
          aria-label="Ask for a shift"
          disabled={loading}
        />
        <button
          type="submit"
          className={styles.submit}
          disabled={loading || instruction.trim().length === 0}
        >
          {loading ? 'thinking' : 'ask'}
        </button>
      </form>
    </div>
  );
};

ShiftBarComponent.displayName = 'ShiftBar';

export const ShiftBar = React.memo(ShiftBarComponent);
